import { useState } from "react";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { NotificationCard } from "./NotificationCard";
import { X, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface NotificationPanelProps {
  closePanel: () => void;
}

const NOTIFICATIONS_PER_PAGE = 5;

export function NotificationPanel({ closePanel }: NotificationPanelProps) {
  const { notifications, unreadCount, loading, markAllAsRead } =
    useNotifications();
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredNotifications =
    filter === "all" ? notifications : notifications?.filter((n) => !n.is_read);

  const totalNotifications = filteredNotifications?.length || 0;
  const totalPages = Math.ceil(totalNotifications / NOTIFICATIONS_PER_PAGE);

  const paginatedNotifications = filteredNotifications?.slice(
    (currentPage - 1) * NOTIFICATIONS_PER_PAGE,
    currentPage * NOTIFICATIONS_PER_PAGE,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilter: "all" | "unread") => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  return (
    <div className="bg-background border rounded-md max-h-[80vh] flex flex-col w-96">
      <div className="flex items-center justify-between p-4 bg-muted/40 border-b">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="px-1.5 py-0.5">
              {unreadCount}
            </Badge>
          )}
          {totalNotifications > 0 && (
            <span className="text-xs text-muted-foreground">
              ({totalNotifications} total)
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => markAllAsRead()}
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Mark all read
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={closePanel}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex p-2 bg-muted/20 border-b">
        <Button
          variant={filter === "all" ? "secondary" : "ghost"}
          size="sm"
          className="flex-1 rounded-r-none text-xs h-7"
          onClick={() => handleFilterChange("all")}
        >
          All
        </Button>
        <Button
          variant={filter === "unread" ? "secondary" : "ghost"}
          size="sm"
          className="flex-1 rounded-l-none text-xs h-7"
          onClick={() => handleFilterChange("unread")}
        >
          Unread
        </Button>
      </div>

      <ScrollArea className="flex-1 max-h-[400px]">
        {loading ? (
          <div className="p-6 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-muted h-4 w-3/4 rounded-md mb-2" />
                <div className="bg-muted h-12 w-full rounded-md" />
              </div>
            ))}
          </div>
        ) : paginatedNotifications && paginatedNotifications.length > 0 ? (
          <div className="py-1">
            {paginatedNotifications.map((notification, i) => (
              <div key={notification.id}>
                <NotificationCard notification={notification} />
                {i < paginatedNotifications.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {filter === "unread"
                ? "No unread notifications"
                : "No notifications"}
            </p>
          </div>
        )}
      </ScrollArea>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between p-3 border-t bg-muted/20">
          <div className="text-xs text-muted-foreground">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>

            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 w-7 p-0 text-xs"
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}

            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
